/**
 * La evidencia del traspaso, para que alguien pueda LEERLA.
 *
 * POR QUÉ EXISTE. Dos cosas se exigían, se subían y se guardaban, y ninguna
 * pantalla las mostraba nunca:
 *
 * 1. LA MARCA DE DAÑO. `rental_inspection_images.is_damage` se escribe cuando
 *    alguien señala un golpe sobre la foto que lo muestra. Después nadie la
 *    leía. Un huésped que marcaba un rayón al recibir el carro estaba haciendo
 *    un gesto sin destinatario: la información entraba a la base y moría ahí.
 *    Y la devolución es justo el momento en que esa marca decide quién paga.
 *
 * 2. LAS DOS FIRMAS. Se exigían para poder enviar la entrega, viajaban al
 *    bucket `signatures` y se escribían en la fila — sin una sola pantalla que
 *    las mostrara. `mapInspectionRow` ni siquiera las mapeaba al dominio.
 *
 * Evidencia que nadie puede consultar no es evidencia: es almacenamiento. Este
 * bloque es el lugar donde las dos partes ven lo que quedó registrado, que es
 * lo que hace que registrarlo signifique algo.
 *
 * SE MUESTRAN LAS DOS INSPECCIONES JUNTAS a propósito. La pregunta real que
 * alguien trae acá nunca es "¿qué decía la entrega?" sino "¿qué cambió entre
 * la entrega y la devolución?".
 */
import { View, Text } from 'react-native'
import { SvgUri } from 'react-native-svg'
import { useTranslation } from 'react-i18next'
import { Image } from '@/components/ui/Image'
import { FONTS, RADIUS, SPACING } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { LICENSE_PHOTO_AREA, type InspectionDetail } from '@/lib/types/inspection.types'

interface InspectionEvidenceBlockProps {
  checkinInspection: InspectionDetail | null | undefined
  checkoutInspection: InspectionDetail | null | undefined
}

export function InspectionEvidenceBlock({
  checkinInspection,
  checkoutInspection,
}: InspectionEvidenceBlockProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  // Sin ninguna de las dos no hay nada que contar, y una tarjeta vacía es ruido.
  if (!checkinInspection && !checkoutInspection) return null

  const etapas = [
    {
      clave: 'checkin' as const,
      titulo: t('inspection_evidence.checkin'),
      dato: checkinInspection,
    },
    {
      clave: 'checkout' as const,
      titulo: t('inspection_evidence.checkout'),
      dato: checkoutInspection,
    },
  ]

  return (
    <View
      style={{
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.surface,
        gap: SPACING.md,
      }}
      testID="trip-detail.inspection-evidence"
    >
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 15, color: colors.textPrimary }}>
        {t('inspection_evidence.title')}
      </Text>

      {etapas.map(({ clave, titulo, dato }) => {
        /*
         * La licencia queda fuera de la cuenta y de la galería.
         *
         * Comparte el arreglo de fotos porque reusa la misma cámara, pero es el
         * documento de identidad de una persona: no es una foto del carro y no
         * tiene por qué aparecer en un resumen que las dos partes miran.
         */
        const fotos = (dato?.images ?? []).filter((img) => img.area !== LICENSE_PHOTO_AREA)
        const danos = fotos.filter((img) => img.isDamage)
        const firmas = [dato?.hostSignatureUrl, dato?.guestSignatureUrl].filter(Boolean).length

        return (
          <View key={clave} style={{ gap: SPACING.sm }} testID={`trip-detail.evidence-${clave}`}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
              <Text
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: FONTS.bodyBold,
                  fontSize: 14,
                  color: colors.textPrimary,
                }}
                numberOfLines={1}
              >
                {titulo}
              </Text>
              <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textSecondary }}>
                {dato ? t('inspection_evidence.photos', { count: fotos.length }) : ''}
              </Text>
            </View>

            {!dato ? (
              <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textMuted }}>
                {t('inspection_evidence.pending')}
              </Text>
            ) : (
              <>
                {/* LOS DAÑOS, con la foto que los muestra al lado del número.
                    Decir "2 con daño marcado" sin enseñarlas volvería a dejar
                    la marca sin destinatario, que es el defecto original. */}
                <Text
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 13,
                    color: danos.length > 0 ? colors.warning : colors.textSecondary,
                  }}
                >
                  {danos.length > 0
                    ? t('inspection_evidence.damages', { count: danos.length })
                    : t('inspection_evidence.no_damages')}
                </Text>

                {danos.length > 0 && (
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.xs }}>
                    {danos.map((img) => (
                      <Image
                        key={img.id}
                        source={img.url}
                        width={72}
                        height={72}
                        borderRadius={RADIUS.sm}
                        contentFit="cover"
                        accessibilityLabel={t(`checkin.photo_${img.area}`)}
                      />
                    ))}
                  </View>
                )}

                {/* LAS FIRMAS. El SVG se pinta de verdad: decir "firmada" sin
                    mostrarla es la misma promesa sin respaldo que el pad de
                    firma ya tenía cuando restauraba un borrador en blanco. */}
                <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textSecondary }}>
                  {firmas === 2
                    ? t('inspection_evidence.signed_both')
                    : firmas === 1
                      ? t('inspection_evidence.signed_one')
                      : t('inspection_evidence.signed_none')}
                </Text>

                {firmas > 0 && (
                  <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
                    {(
                      [
                        {
                          url: dato.hostSignatureUrl,
                          quien: t('inspection_evidence.signature_host'),
                        },
                        {
                          url: dato.guestSignatureUrl,
                          quien: t('inspection_evidence.signature_guest'),
                        },
                      ] as const
                    ).map(({ url, quien }) =>
                      url ? (
                        <View
                          key={quien}
                          style={{
                            flex: 1,
                            gap: 2,
                            padding: SPACING.xs,
                            borderRadius: RADIUS.sm,
                            borderWidth: 1,
                            borderColor: colors.border,
                            backgroundColor: colors.surfaceSubtle,
                          }}
                        >
                          {/* `fallback` no es decoración: sin él, una firma que
                              no baja o un SVG que no parsea renderizan `null`, y
                              el recuadro queda vacío justo al lado de un texto
                              que afirma "Firmada por las dos partes". La
                              evidencia se ve ausente sin que nadie lo diga. */}
                          <SvgUri
                            uri={url}
                            width="100%"
                            height={56}
                            fallback={
                              <Text
                                style={{
                                  fontFamily: FONTS.body,
                                  fontSize: 12,
                                  color: colors.textMuted,
                                  textAlign: 'center',
                                  paddingVertical: SPACING.sm,
                                }}
                              >
                                {t('inspection_evidence.signature_unavailable')}
                              </Text>
                            }
                          />
                          <Text
                            style={{
                              fontFamily: FONTS.body,
                              fontSize: 12,
                              color: colors.textMuted,
                              textAlign: 'center',
                            }}
                          >
                            {quien}
                          </Text>
                        </View>
                      ) : null,
                    )}
                  </View>
                )}
              </>
            )}
          </View>
        )
      })}
    </View>
  )
}
